import { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Plus, Link2, LogOut, Settings, Users, FileText, CreditCard, Gift } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { cn } from '../lib/cn';
import { Logo } from '../components/Logo';
import { useBusinesses, useActiveBusiness } from '../api/hooks';
import { useSession } from '../stores/session';

/**
 * Mercury-pattern business switcher.
 *
 * Sits in the top-left of the top bar. Clicking opens a dropdown with:
 *   1. Current business header card (logo + name + plan badge)
 *   2. Account-level links (Settings, Users, Documents, Plan & Billing, Referrals)
 *   3. "All Accounts" — list of every business this user belongs to with a check on active
 *   4. "Apply for new" / "Link existing" / "Log out"
 */
export function BusinessSwitcher() {
  const { data: allBusinesses = [] } = useBusinesses();
  const { data: currentBusiness } = useActiveBusiness();
  const setActiveBusiness = useSession((s) => s.setActiveBusiness);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClick);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onClick);
        document.removeEventListener('keydown', onKey);
      };
    }
    return undefined;
  }, [open]);

  if (!currentBusiness) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-2 px-2 py-1.5 -ml-2 rounded-md transition',
          'hover:bg-zinc-100 dark:hover:bg-zinc-800/70',
          open && 'bg-zinc-100 dark:bg-zinc-800/70',
        )}
      >
        <Logo />
        <span className="font-semibold text-zinc-950 dark:text-zinc-50 tracking-tight">{currentBusiness.name}</span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-zinc-500 dark:text-zinc-400 transition-transform',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          role="menu"
          className={cn(
            'absolute left-0 mt-1.5 z-50 w-80',
            'rounded-xl bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 shadow-lg',
            'overflow-hidden',
          )}
        >
          {/* Current business card */}
          <div className="p-3 border-b border-zinc-200 dark:border-zinc-700">
            <div className="flex items-center gap-3 p-2">
              <span className={cn('w-9 h-9 rounded-lg grid place-items-center', currentBusiness.iconColor)}>
                <svg viewBox="0 0 24 24" className="w-5 h-5 text-white dark:text-zinc-950" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 4h9a5 5 0 0 1 0 10H8v6" />
                </svg>
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-zinc-950 dark:text-zinc-50 truncate">{currentBusiness.name}</span>
                  {currentBusiness.isPro && (
                    <span className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-950 text-white dark:bg-zinc-50 dark:text-zinc-950">
                      {currentBusiness.plan}
                    </span>
                  )}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">{currentBusiness.subtitle}</div>
              </div>
            </div>
          </div>

          {/* Account-level links */}
          <div className="p-1.5">
            <MenuItem to="/profile" icon={<Settings className="w-4 h-4" />} label="All Settings" />
            <MenuItem to="/team" icon={<Users className="w-4 h-4" />} label="Users" />
            <MenuItem to="/billing" icon={<FileText className="w-4 h-4" />} label="Documents & Data" />
            <MenuItem to="/subscription" icon={<CreditCard className="w-4 h-4" />} label="Plan & Billing" />
            <MenuItem
              icon={<Gift className="w-4 h-4" />}
              label="Referrals"
              right={
                <span className="px-1.5 py-px rounded text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                  Earn ৳500
                </span>
              }
            />
          </div>

          {/* All Accounts */}
          <div className="px-3 pt-3 pb-1">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 px-2 mb-1">
              All Accounts
            </div>
          </div>
          <div className="px-1.5 pb-1.5">
            {allBusinesses.map((b) => {
              const isActive = b.id === currentBusiness.id;
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => { setActiveBusiness(b.id); setOpen(false); }}
                  className={cn(
                    'w-full flex items-center gap-3 px-2 py-2 rounded-md transition text-left',
                    'hover:bg-zinc-50 dark:hover:bg-zinc-800',
                    isActive && 'bg-zinc-50 dark:bg-zinc-800',
                  )}
                >
                  <span className={cn('w-7 h-7 rounded-lg grid place-items-center flex-shrink-0', b.iconColor)}>
                    <svg viewBox="0 0 24 24" className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 4h9a5 5 0 0 1 0 10H8v6" />
                    </svg>
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-950 dark:text-zinc-50 truncate">{b.name}</div>
                    <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{b.subtitle}</div>
                  </div>
                  {isActive && <Check className="w-4 h-4 text-brand dark:text-brand-dm flex-shrink-0" />}
                </button>
              );
            })}
          </div>

          {/* Footer actions */}
          <div className="p-1.5 border-t border-zinc-200 dark:border-zinc-700">
            <MenuItem to="/businesses/new" icon={<Plus className="w-4 h-4" />} label="Apply for a new business" />
            <MenuItem icon={<Link2 className="w-4 h-4" />} label="Link an existing business" />
          </div>
          <div className="p-1.5 border-t border-zinc-200 dark:border-zinc-700">
            <MenuItem to="/logout" icon={<LogOut className="w-4 h-4" />} label="Log out" />
          </div>
        </div>
      )}
    </div>
  );
}

function MenuItem({
  to,
  icon,
  label,
  right,
}: {
  to?: string;
  icon: React.ReactNode;
  label: string;
  right?: React.ReactNode;
}) {
  const cls = cn(
    'w-full flex items-center gap-3 px-2.5 py-2 rounded-md text-sm text-zinc-700 dark:text-zinc-200 transition',
    'hover:bg-zinc-50 dark:hover:bg-zinc-800',
  );
  const inner = (
    <>
      <span className="text-zinc-500 dark:text-zinc-400">{icon}</span>
      <span className="flex-1 text-left">{label}</span>
      {right}
    </>
  );
  if (to) {
    return (
      <Link to={to} role="menuitem" className={cls}>
        {inner}
      </Link>
    );
  }
  return (
    <button type="button" role="menuitem" className={cls}>
      {inner}
    </button>
  );
}
